/**
 * VariationTab — problem-hub 문제 상세 [변형] 탭 (⑤ AI 문제 변형, 단건).
 *
 * 계약: BE-20260825-ai-variation-screens.md §3(요청 옵션)·§6(화면 제안).
 * 상단 요청 바(강도 L1~L4 + 고급 접기) / 하단 이 문제의 변형 목록 → 고르면 검수 카드가 펼쳐진다.
 * 한 원본을 여러 번·여러 강도로 변형할 수 있다(후보를 여러 개 뽑아 고르는 용도라 중복 방지 없음).
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Sparkles, ChevronDown, ChevronRight, RefreshCw, ArrowUpRight, GitBranch } from 'lucide-react';
import { Button, AiRunModal } from '../common';
import { useAuth } from '../../context/AuthContext';
import { formatV2Timestamp } from '../../utils/v2DateTime';
import VariationReviewCard from './VariationReviewCard';
import { fetchProblem } from './problemApi';
// 계보 칩 안의 번호도 표와 같은 얼굴을 쓴다(오너 결정 2026-09-01) — 색은 칩이 갖고,
// 이 부품에서는 고정폭·굵기만 가져온다(chip).
import ProblemIdLink from './ProblemIdLink';
import {
  requestVariation, fetchProblemVariations, rejectVariation, retryVariation,
  getJobStatusDisplay, getReviewStatusDisplay, getTypeLabel,
  LEVELS, ANSWER_TYPE_OPTIONS, POLLING_STATUSES, JOB_POLL_MS, AUTH_REDIRECT } from './variationApi';

/**
 * 승인으로 만들어진 문제 번호. **`approved_problem_id` 만 본다.**
 * 변형 행의 다른 `*_id` 를 폴백으로 쓰지 않는다 — 그것이 원본을 가리키는 값이면
 * 「등록된 문제 열기」가 원본으로 되돌아가는 거짓 링크가 된다. 번호를 모르면 안 그린다.
 */
const approvedIdOf = (it) => it?.approved_problem_id ?? null;

const fmt = (iso) => formatV2Timestamp(iso, 'shortDateTime');

/**
 * @param canRunAi [변형 만들기]·[다시 생성] 을 세울지 — 서버는 열려 있고 **LLM 비용** 때문에 화면에서만 감춘다.
 * @param canApprove [이 변형 폐기] 와 검수 카드의 승인·폐기를 세울지 — 서버가 교사에게 403 이다.
 *
 * 담는 쪽(pages/ProblemHub.js)은 이 탭을 **둘 다 거짓이면 아예 세우지 않는다** — 남는 것이
 * 목록 읽기뿐이라 빈 껍데기가 되기 때문이다. 그래도 이 탭이 스스로 한 겹 더 가르는 이유:
 * 나중에 한쪽만 열리는 날(오너가 AI 실행을 교사에게 여는 날) 탭은 서고 승인만 막혀야 한다.
 * 기본값이 허용(true)인 것은 형제 부품과 같은 이유다.
 */
function VariationTab({
  problemId, onApproved = null, onOpenProblem = null, onDirtyChange = null,
  canRunAi = true, canApprove = true,
}) {
  const { getAccessToken } = useAuth();
  const [level, setLevel] = useState(2); // 기본 L2(표현 재서술) — 원본 구조를 지키면서 쓸모가 있는 지점
  const [advOpen, setAdvOpen] = useState(false);
  const [optDifficulty, setOptDifficulty] = useState('keep');
  const [optAnswerType, setOptAnswerType] = useState('keep');
  const [requesting, setRequesting] = useState(false);
  // AI 요청 창 — [변형 만들기]가 바로 실행하지 않고 이것을 연다.
  // 미저장 확인(window.confirm)도 이 창의 경고 띠로 옮겼다 — 둘 다 두면 창이 두 번 뜬다.
  const [aiOpen, setAiOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [toast, setToast] = useState(null);
  // 열려 있는 카드의 미저장 여부 — 접거나 다른 행을 열 때 조용히 버리지 않기 위해 부모가 쥔다
  const dirtyRef = useRef(false);
  const targetRef = useRef(problemId);
  useEffect(() => { targetRef.current = problemId; }, [problemId]);
  // 같은 문제의 요청끼리도 겹친다(폴링 tick 과 [변형 만들기] 직후 갱신). 늦게 온 옛 응답이
  // 방금 만든 변형을 지워 버리면 폴링 근거까지 사라져 화면이 조용히 멈춘다.
  const seqRef = useRef(0);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (problemId == null) return;
    const target = problemId;
    const seq = (seqRef.current += 1);
    if (!silent) { setLoading(true); setError(null); }
    try {
      const list = await fetchProblemVariations(target, getAccessToken);
      if (seq !== seqRef.current || String(target) !== String(targetRef.current)) return;
      setItems(list);
    } catch (e) {
      if (seq !== seqRef.current || String(target) !== String(targetRef.current)) return;
      if (silent) {
        // 목록을 지우지는 않되 알린다 — 이 목록이 곧 폴링의 근거라, 조용히 실패하면
        // 진행 중인 건이 화면에 나타나지도 않고 폴링도 시작되지 않는다.
        setToast({ type: 'error', text: '목록을 갱신하지 못했습니다 — 새로고침해 주세요.' });
        return;
      }
      setError(e.message || '변형 이력을 불러오지 못했습니다.');
      setItems([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [problemId, getAccessToken]);

  useEffect(() => { setOpenId(null); setToast(null); setShowRejected(false); dirtyRef.current = false; load(); }, [load]);

  /**
   * 계보의 **위쪽** — 이 문제가 변형으로 태어났으면 그 원본이 있다(`origin_parent_id`).
   *
   * 상세를 여기서 한 번 더 읽는다. [조회] 탭이 이미 읽고 있지만 그 탭이 **늘 마운트돼 있지는
   * 않다** — 문제를 넘길 때 활성 탭만 남으므로, 변형 탭에 머무른 채 다른 문제를 열면 상세를
   * 아는 곳이 하나도 없다. 실패하면 조용히 접는다(계보는 보조 정보다).
   */
  const [sourceProblem, setSourceProblem] = useState(null);
  useEffect(() => {
    if (problemId == null) { setSourceProblem(null); return undefined; }
    let alive = true;
    setSourceProblem(null);
    Promise.resolve(fetchProblem(problemId, getAccessToken))
      .then((d) => { if (alive) setSourceProblem(d ?? null); })
      .catch(() => { /* 계보 줄만 접힌다 */ });
    return () => { alive = false; };
  }, [problemId, getAccessToken]);
  const originParentId = sourceProblem?.origin_parent_id ?? null;

  /** 계보의 **아래쪽** — 승인돼 은행에 등록된 변형들 */
  const descendants = useMemo(
    () => items.map(approvedIdOf).filter((v) => v != null),
    [items],
  );
  /** 승인은 됐는데 번호를 목록에서 알 수 없는 건 — 있는 그대로 말한다(지어내지 않는다) */
  const approvedUnknown = useMemo(
    () => items.filter((it) => it.review_status === 200 && approvedIdOf(it) == null).length,
    [items],
  );

  /**
   * 폐기(review_status 400)는 **기본으로 감춘다**(오너 결정 2026-08-30).
   *
   * 한 원본에서 후보를 여러 번 뽑는 것이 이 탭의 쓰임이라 폐기가 계속 쌓이고, 그것이 섞여 있으면
   * 「N건」이 검수할 것의 개수를 거짓으로 말한다.
   *
   * 지우지 않고 **접어 두는** 이유 셋: ① 폐기는 되돌릴 수 없다(복구 API가 없다) — 잘못 눌렀을 때
   * 무엇을 버렸는지 확인할 자리가 화면에서 사라지면 안 된다. ② 「이 강도는 이미 해 봤다」가 남아야
   * 같은 요청을 되풀이하지 않는다(건당 수십 초 + AI 비용). ③ 멈춰서 치운 것과 결과가 나빠 버린 것이
   * 같은 상태값이라, 둘을 가르려면 목록에 있어야 한다.
   *
   * ⚠ 거르는 것은 **렌더뿐**이고 `items` 자체는 그대로 둔다 — 그 배열이 폴링의 근거(anyRunning)라,
   *   원본에 필터를 걸면 진행 중인 건을 놓쳐 화면이 조용히 멈춘다.
   */
  const [showRejected, setShowRejected] = useState(false);
  const rejectedCount = useMemo(() => items.filter((it) => it.review_status === 400).length, [items]);
  const visibleItems = useMemo(
    () => (showRejected ? items : items.filter((it) => it.review_status !== 400)),
    [items, showRejected],
  );
  const toggleRejected = () => {
    const next = !showRejected;
    // 접는 쪽으로 갈 때, 열려 있는 행이 폐기 건이면 함께 닫는다 — 보이지 않는 카드가 열린 채
    // 남으면 아래에 정체를 알 수 없는 카드만 떠 있게 된다. 폐기 건은 편집이 잠겨 미저장분이 없다.
    if (!next && openId != null) {
      const cur = items.find((it) => String(it.variation_id ?? it.id) === String(openId));
      if (cur?.review_status === 400) setOpenId(null);
    }
    setShowRejected(next);
  };

  // 카드를 접거나 다른 행으로 옮길 때 미저장 편집이 있으면 묻는다(카드는 언마운트되며 사라진다)
  const switchOpen = (nextId) => {
    if (dirtyRef.current && openId != null && String(nextId) !== String(openId)) {
      // eslint-disable-next-line no-alert
      if (!window.confirm('저장하지 않은 수정이 있습니다. 버리고 이동할까요?')) return;
    }
    setOpenId(nextId); // 해제는 카드의 언마운트 정리가 알린다
  };

  // 목록에 진행 중인 건이 있으면 목록도 함께 폴링한다 — 카드를 열지 않아도 상태가 살아 움직여야 한다
  const anyRunning = items.some((it) => POLLING_STATUSES.includes(it.status));
  useEffect(() => {
    if (!anyRunning) return undefined;
    const t = setInterval(() => load({ silent: true }), JOB_POLL_MS);
    return () => clearInterval(t);
  }, [anyRunning, load]);

  const [rejecting, setRejecting] = useState(null);

  /** 목록에서 바로 폐기 — 멈춘 건을 치우는 용도라 확인을 한 번 받는다 */
  const discard = async (vid) => {
    if (rejecting != null) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('이 변형을 폐기할까요? 되돌릴 수 없습니다.')) return;
    setRejecting(vid);
    try {
      const d = await rejectVariation(vid, getAccessToken);
      if (d === AUTH_REDIRECT) return; // 401 → /signin 이동 중
      if (String(vid) === String(openId)) setOpenId(null);
      await load({ silent: true });
    } catch (e) {
      setToast({ type: 'error', text: e.message || '폐기에 실패했습니다.' });
    } finally {
      setRejecting(null);
    }
  };

  /**
   * 실패한 건 다시 돌리기 — 계약 §2 는 재시도를 **실패(500)에만** 연다.
   * 카드 안에도 [다시 생성]이 있지만, 실패한 건은 카드를 열어도 보여 줄 결과가 없다.
   * 그래서 목록에서 바로 누를 수 있어야 한다.
   */
  const [retrying, setRetrying] = useState(null);
  const retryOne = async (vid) => {
    if (retrying != null) return;
    setRetrying(vid);
    setToast(null);
    try {
      const d = await retryVariation(vid, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      setToast({ type: 'info', text: '다시 생성을 요청했습니다 — 수십 초 걸립니다.' });
      await load({ silent: true });
    } catch (e) {
      setToast({ type: 'error', text: e.message || '재시도에 실패했습니다.' });
      // 다른 창에서 먼저 폐기했다(회신 §2) — 다시 읽어 눌러도 안 되는 버튼을 걷는다
      if (e?.data?.reason === 'rejected') await load({ silent: true });
    } finally {
      setRetrying(null);
    }
  };

  const handleRequest = async (userPrompt) => {
    if (requesting || problemId == null) return;
    // 미저장 경고는 창의 warning 띠가 이미 보여 줬다(dirtyRef). 여기서 다시 묻지 않는다.
    // 표시를 내리지 않는 이유는 종전과 같다 — 요청이 실패하면 카드는 그대로 남는데 편집만
    // 살아 있고 표시가 풀린 상태가 된다. 해제는 카드가 언마운트되며 스스로 알린다.
    setRequesting(true);
    setToast(null);
    try {
      const options = {};
      if (optDifficulty !== 'keep') options.difficulty = optDifficulty;
      if (optAnswerType !== 'keep') options.answer_type = optAnswerType;
      const d = await requestVariation(problemId, { level, options, userPrompt }, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      setAiOpen(false);
      setToast({ type: 'info', text: '변형을 요청했습니다 — 수십 초 걸립니다.' });
      if (d.variation_id != null) setOpenId(d.variation_id);
      await load({ silent: true });
    } catch (e) {
      setToast({ type: 'error', text: e.message || '변형 요청에 실패했습니다.' });
    } finally {
      setRequesting(false);
    }
  };

  if (problemId == null) return null;

  const chip = 'inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors';

  return (
    <div className="space-y-3">
      {/* 계보 — 조상(원본) ← 이 문제 → 후손(등록된 변형). 두 방향을 한 줄에 둔다.
          변형은 문제를 **복제해 늘리는** 기능이라, 지금 보는 문제가 그 사슬의 어디쯤인지
          모르면 같은 문제를 두 번 변형하거나 원본을 잃는다. [문제 보기] 탭 머리의 [원본 #N]과
          같은 규격을 쓰되, 그쪽에 없는 **아래 방향**을 여기서 갖는다. */}
      {(originParentId != null || descendants.length > 0 || approvedUnknown > 0) && (
        <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-gray-100 bg-gray-50 px-2.5 py-1.5">
          <GitBranch aria-hidden="true" className="size-3.5 shrink-0 text-gray-400" />
          <span className="shrink-0 text-[11px] font-medium text-gray-500">계보</span>

          {originParentId != null && (
            <>
              {onOpenProblem ? (
                <button
                  type="button"
                  onClick={() => onOpenProblem(originParentId)}
                  title="이 문제의 원본 열기"
                  className={`${chip} bg-gray-200 text-gray-700 hover:bg-gray-300 hover:text-gray-900`}
                >
                  원본 <ProblemIdLink id={originParentId} chip linked={false} />
                  <ArrowUpRight aria-hidden="true" className="size-3" />
                </button>
              ) : (
                <span className={`${chip} bg-gray-200 text-gray-700`}>
                  원본 <ProblemIdLink id={originParentId} chip linked={false} />
                </span>
              )}
              <span aria-hidden="true" className="text-[10px] text-gray-300">→</span>
            </>
          )}

          {/* 지금 보고 있는 문제 — 링크가 아니다(제자리로 가는 길은 길이 아니다) */}
          <span className={`${chip} border border-primary/30 bg-primary/10 text-primary`}>
            이 문제 <ProblemIdLink id={problemId} chip linked={false} />
          </span>

          {(descendants.length > 0 || approvedUnknown > 0) && (
            <span aria-hidden="true" className="text-[10px] text-gray-300">→</span>
          )}
          {descendants.map((pid) => (onOpenProblem ? (
            <button
              key={pid}
              type="button"
              onClick={() => onOpenProblem(pid)}
              title="이 문제에서 만들어진 변형 열기"
              className={`${chip} bg-green-50 text-green-700 hover:bg-green-100 hover:text-green-900`}
            >
              <ProblemIdLink id={pid} chip linked={false} />
              <ArrowUpRight aria-hidden="true" className="size-3" />
            </button>
          ) : (
            <span key={pid} className={`${chip} bg-green-50 text-green-700`}>
              <ProblemIdLink id={pid} chip linked={false} />
            </span>
          )))}
          {/* 번호를 모르는 승인 건 — 「없다」고 말하지 않는다. 아래 목록에서 그 행을 열면 나온다. */}
          {approvedUnknown > 0 && (
            <span className="text-[10px] text-gray-400">
              승인 {approvedUnknown}건은 아래 목록에서 행을 열어야 번호가 보입니다
            </span>
          )}
        </div>
      )}

      {/* 요청 바 */}
      <div className="rounded-xl border border-gray-200 p-3">
        <div className="mb-2 flex items-center gap-1.5">
          <Sparkles aria-hidden="true" className="size-4 text-violet-500" />
          <span className="text-sm font-bold text-gray-800">변형 강도</span>
        </div>
        {/* 두 칸 격자로 고정한다 — 종전의 sm:grid-cols-4 는 **화면 폭**(640px)을 보는 분기점이라, 문제 상세가
            두 칸일 때 이 탭이 서는 오른쪽 칸이 340px 남짓이어도 늘 네 칸으로 서서 설명이 네 줄씩 꺾였다
            (2026-09-11 실측: 오른쪽 칸 348px 에서 칸 하나 68px). 이 부품은 제 칸 폭을 모르므로 한 벌로 둔다. */}
        <div className="grid grid-cols-2 gap-1.5" role="group" aria-label="변형 강도">
          {LEVELS.map((l) => (
            <button
              key={l.value}
              type="button"
              onClick={() => setLevel(l.value)}
              aria-pressed={level === l.value}
              title={l.hint}
              className={`rounded-lg border px-2 py-1.5 text-left transition-colors ${
                level === l.value ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-primary/40'
              }`}
            >
              <span className={`block text-xs font-bold ${level === l.value ? 'text-primary' : 'text-gray-700'}`}>
                L{l.value} {l.label}
              </span>
              <span className="mt-0.5 block text-[10px] leading-tight text-gray-400">{l.hint}</span>
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setAdvOpen((v) => !v)}
          aria-expanded={advOpen}
          aria-controls="variation-adv"
          className="mt-2 flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-gray-700"
        >
          {advOpen ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
          고급 — 난이도·문제 유형
        </button>
        {advOpen && (
          <div id="variation-adv" className="mt-1.5 space-y-1.5 rounded-lg bg-gray-50 p-2">
            <label className="flex items-center gap-2 text-[11px] text-gray-600">
              <span className="w-16 shrink-0">난이도</span>
              <select
                value={optDifficulty}
                onChange={(e) => setOptDifficulty(e.target.value)}
                className="min-w-0 flex-1 rounded border px-1.5 py-1 text-xs focus:border-primary focus:outline-none"
              >
                <option value="keep">유지</option>
                <option value="up">한 단계 올림</option>
                <option value="down">한 단계 내림</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-[11px] text-gray-600">
              <span className="w-16 shrink-0">문제 유형</span>
              <select
                value={optAnswerType}
                onChange={(e) => setOptAnswerType(e.target.value)}
                className="min-w-0 flex-1 rounded border px-1.5 py-1 text-xs focus:border-primary focus:outline-none"
              >
                {ANSWER_TYPE_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
            </label>
            <p className="text-[10px] text-gray-400">
              두 옵션은 AI에게 주는 유도값이라 100% 보장되지 않습니다 — 결과는 검수에서 확인하세요.
            </p>
          </div>
        )}

        {canRunAi && (
          <div className="mt-2 flex items-center gap-2">
            <Button size="sm" onClick={() => setAiOpen(true)} loading={requesting} disabled={requesting} icon={<Sparkles className="size-3.5" />}>
              변형 만들기
            </Button>
            <span className="text-[11px] text-gray-400">원본은 그대로 두고 새 문제 후보가 생깁니다.</span>
          </div>
        )}
        <p
          role={toast?.type === 'error' ? 'alert' : 'status'}
          aria-live={toast?.type === 'error' ? 'assertive' : 'polite'}
          className={`mt-1.5 text-xs ${toast ? (toast.type === 'error' ? 'text-danger' : 'text-gray-600') : 'sr-only'}`}
        >
          {toast?.text || ''}
        </p>

        {/* 이 탭은 모달이 아니라 패널이라 창이 겹치지 않는다 — 부모의 ESC 를 끌 필요가 없다.
            canRunAi 를 창에도 건다 — 여는 버튼만 감추면 창 자체는 살아 있다(형제 카드와 같은 이유). */}
        <AiRunModal
          isOpen={aiOpen && canRunAi}
          title="변형 요청"
          summary={`강도 L${level} 로 이 문제의 변형을 하나 만듭니다. 수십 초 걸리고, 원본은 그대로 둡니다.`}
          warning={dirtyRef.current
            ? '저장하지 않은 수정이 있습니다 — 새 변형을 요청하면 그 수정은 버려집니다.'
            : null}
          placeholder="예: 그래프는 그대로 두고 수치만 · 실생활 소재로"
          confirmText="변형 만들기"
          busy={requesting}
          /* 「옵션·규칙이 지시를 이긴다」 안내를 켠다(BE-20260829-reply-fe-0828.md §4-2 5-1).
             이 자리는 켤 자격이 있다 — 위 handleRequest 가 강도(level)와 고급 옵션
             (difficulty·answer_type)을 지시와 **같은 요청 몸통에** 실어 보낸다.
             그래서 난이도를 「한 단계 올림」으로 두고 지시에 「쉽게」라고 쓰면 난이도는 올라가고
             「쉽게」는 조용히 무시된다. 화면이 그 말을 안 하면 사람은 AI 가 말을 안 들었다고 읽는다.
             고급을 「유지」로 두면 options 는 실리지 않지만 강도는 언제나 실린다 — 안내가 가리킬
             옵션이 사라지는 순간은 없다. */
          showOptionPriority
          onConfirm={handleRequest}
          onClose={() => setAiOpen(false)}
        />
      </div>

      {/* 이 문제의 변형 목록 */}
      <div>
        <div className="mb-1.5 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500">이 문제의 변형</span>
          {/* 개수는 **보이는 것**을 센다 — 폐기를 섞어 세면 검수할 것이 몇 개인지 화면이 거짓말한다 */}
          <span className="text-[11px] text-gray-400">{loading ? '읽는 중…' : `${visibleItems.length}건`}</span>
          {!loading && rejectedCount > 0 && (
            <button
              type="button"
              onClick={toggleRejected}
              aria-pressed={showRejected}
              className="text-[11px] text-gray-400 underline hover:text-gray-600"
            >
              {showRejected ? `폐기 ${rejectedCount}건 숨기기` : `폐기 ${rejectedCount}건 보기`}
            </button>
          )}
          <button
            type="button"
            onClick={() => load()}
            disabled={loading}
            aria-label="변형 목록 새로고침"
            title="새로고침"
            className="ml-auto rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
          >
            <RefreshCw className={`size-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {error && (
          <div role="alert" className="space-y-2 rounded-xl border border-red-100 bg-red-50 p-3">
            <p className="text-sm text-red-700">{error}</p>
            <Button size="xs" variant="secondary" onClick={() => load()}>다시 시도</Button>
          </div>
        )}
        {!error && loading && visibleItems.length === 0 && (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => <div key={i} className="h-12 rounded-xl bg-gray-100 motion-safe:animate-pulse" />)}
          </div>
        )}
        {!error && !loading && visibleItems.length === 0 && (
          <p className="rounded-xl border border-gray-100 p-4 text-center text-sm text-gray-400">
            {/* 없는 버튼을 가리키지 않는다 — [변형 만들기]가 감춰진 사람에게 그것을 누르라고 하면
                화면이 거짓을 말한다(감춤은 「내가 못 찾는 것」이 아니라 「내 것이 아닌 것」이다). */}
            {items.length === 0
              ? (canRunAi
                ? '아직 변형이 없습니다 — 위에서 강도를 고르고 [변형 만들기]를 눌러 보세요.'
                : '아직 변형이 없습니다.')
              : `만든 변형을 모두 폐기했습니다 — 위 [폐기 ${rejectedCount}건 보기]로 확인할 수 있습니다.`}
          </p>
        )}

        <ul className="space-y-1.5">
          {visibleItems.map((it) => {
            const vid = it.variation_id ?? it.id;
            const st = getJobStatusDisplay(it.status);
            const rv = getReviewStatusDisplay(it.review_status);
            const open = String(openId) === String(vid);
            const typeLabel = getTypeLabel(it.result?.problem);
            // 펼쳐 본 폐기 건은 **한 단 내려** 세운다 — 같은 무게로 서면 감춘 뜻이 없어진다.
            // 내리는 축은 바탕색이고 글자는 건드리지 않는다: 흐리게 만들면 위계가 아니라
            // 읽을 수 없음이 된다(rules/visual-hierarchy §8). 「폐기」 배지가 함께 말한다.
            return (
              <li
                key={vid}
                className={`rounded-xl border ${open ? 'border-primary/40' : 'border-gray-200'} ${
                  it.review_status === 400 ? 'bg-gray-50' : ''
                }`}
              >
                <button
                  type="button"
                  onClick={() => switchOpen(open ? null : vid)}
                  aria-expanded={open}
                  className="flex w-full items-center gap-1.5 p-2 text-left"
                >
                  {open ? <ChevronDown className="size-3.5 shrink-0 text-primary" /> : <ChevronRight className="size-3.5 shrink-0 text-gray-300" />}
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${st.cls}`}>{st.text}</span>
                  {it.level != null && <span className="shrink-0 text-[10px] text-gray-500">L{it.level}</span>}
                  {typeLabel && <span className="shrink-0 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[9px] text-indigo-700">{typeLabel}</span>}
                  {rv && it.review_status !== 100 && (
                    <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] ${rv.cls}`}>{rv.text}</span>
                  )}
                  <span className="min-w-0 flex-1 truncate text-xs text-gray-500">{it.description || ''}</span>
                  <span className="shrink-0 text-[10px] text-gray-400">{fmt(it.created_at)}</span>
                </button>
                {/* 아래 줄에는 두 가지가 선다 — 왼쪽 [폐기], 오른쪽 [등록된 문제 열기].
                    행 자체가 button 이라 그 안에 버튼을 넣을 수 없어(중첩 금지) 이 줄을 쓴다.
                    폐기는 상태 제한이 없다(계약 §2) — 카드는 200·500 에서만 폐기를 내주므로,
                    대기·생성 중에 멈춘 건은 여기가 유일한 출구다. */}
                {/* 조건은 **안에 실제로 설 것이 있는지**로 센다 — 「review_status===100」만 보면
                    폐기가 감춰진 사람에게 빈 띠 한 줄이 남는다(테두리만 있는 줄). */}
                {/* 폐기한 변형은 재시도가 409 다(BE-20261102 회신 §2) — [다시 생성]을 세우지 않는다 */}
                {((it.status === 500 && it.review_status !== 400 && canRunAi)
                  || (it.review_status === 100 && canApprove)
                  || (approvedIdOf(it) != null && onOpenProblem)) && (
                  <div className="flex items-center gap-2 border-t px-2 py-1">
                    {it.status === 500 && it.review_status !== 400 && canRunAi && (
                      <button
                        type="button"
                        onClick={() => retryOne(vid)}
                        disabled={retrying === vid}
                        className="text-[10px] font-medium text-primary underline hover:text-primary/70 disabled:opacity-40"
                      >
                        {retrying === vid ? '요청 중…' : '다시 생성'}
                      </button>
                    )}
                    {it.review_status === 100 && canApprove && (
                      <button
                        type="button"
                        onClick={() => discard(vid)}
                        disabled={rejecting === vid}
                        className="text-[10px] text-gray-400 underline hover:text-danger disabled:opacity-40"
                      >
                        {rejecting === vid ? '폐기 중…' : '이 변형 폐기'}
                      </button>
                    )}
                    {approvedIdOf(it) != null && onOpenProblem && (
                      <button
                        type="button"
                        onClick={() => onOpenProblem(approvedIdOf(it))}
                        title="이 변형으로 등록된 문제 열기"
                        className="ml-auto inline-flex items-center gap-0.5 text-[10px] font-medium text-green-700 underline hover:text-green-900"
                      >
                        등록 <ProblemIdLink id={approvedIdOf(it)} chip linked={false} /> 열기
                        <ArrowUpRight aria-hidden="true" className="size-3" />
                      </button>
                    )}
                  </div>
                )}
                {open && (
                  <div className="border-t p-3">
                    <VariationReviewCard
                      kind="variation"
                      id={vid}
                      /* 원본 대조를 은행 본문으로 그리고, reuse 변형의 그림을 여기서 빌린다.
                         상세를 함께 넘겨 카드가 같은 문제를 또 읽지 않게 한다. */
                      sourceProblemId={problemId}
                      sourceProblem={sourceProblem}
                      canApprove={canApprove}
                      canRunAi={canRunAi}
                      onApproved={onApproved}
                      onOpenProblem={onOpenProblem}
                      onDirtyChange={(d) => { dirtyRef.current = d; onDirtyChange?.(d); }}
                      onChanged={() => load({ silent: true })}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export default VariationTab;
